import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import printJS from 'print-js';
import { styled } from '@mui/material/styles';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import DownloadIcon from '@mui/icons-material/Download';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import MenuItem from '@mui/material/MenuItem';
import { Typography } from '@components/Typography';
import Popper from '@components/Popper';
import Button from '@components/Button';
import { surveyService } from '@services';
import { alertActions } from '@actions';
import { useTranslation, useThrottleValue } from '@hooks';

const PreviewQRImg = styled('img')({
    width: '100%',
    maxWidth: 384,
    '@media (max-width: 600px)': {
        width: 280,
    },
});

const ButtonsWrapper = styled('div')({
    marginTop: 32,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
});

const PreviewCustomization = ({ externalId }) => {
    const [qrImage, setQRImage] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isValid, watch } = useFormContext();

    const mainLocationId = watch('mainLocationId');
    const topText = watch('topText');
    const bottomText = watch('bottomText');
    const backgroundColor = watch('backgroundColor');
    const fillColor = watch('fillColor');
    const qrLogoUrl = watch('qrLogoUrl');
    const customizeQrCodeDesign = watch('customizeQrCodeDesign');
    const followUpEnable = watch('followUpEnable');

    const throttledPreview = useThrottleValue(
        {
            locationId: mainLocationId,
            topText,
            bottomText,
            backgroundColor,
            fillColor,
            qrLogoUrl,
            customizeQrCodeDesign,
            followUpEnable,
        },
        3000,
    );

    const fetchImage = async survey => {
        try {
            const surveyImage = await surveyService.getSurveyImage({
                ...survey,
                logoUrl: survey.qrLogoUrl,
                externalId,
                extension: 'png',
            });
            setQRImage(surveyImage);
        } catch (_) {
            dispatch(alertActions.error(t('alertMessages.createFail')));
        }
    };

    useEffect(() => {
        if (isValid) {
            fetchImage(throttledPreview);
        }
    }, [throttledPreview]);

    const handleOpen = event => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const onPrint = () => {
        try {
            printJS({ printable: qrImage, type: 'image' });
        } catch (_) {
            dispatch(alertActions.error(t('alertMessages.printFail')));
        }
    };

    const onDownload = async ext => {
        try {
            handleClose();
            const surveyImage = await surveyService.getSurveyImage({
                ...throttledPreview,
                logoUrl: throttledPreview.qrLogoUrl,
                externalId,
                extension: ext.toLowerCase(),
            });
            const anchor = document.createElement('a');
            anchor.style.display = 'none';
            anchor.href = surveyImage;
            anchor.download = `QR.${ext.toLowerCase()}`;
            document.body.appendChild(anchor);
            anchor.click();
            window.URL.revokeObjectURL(surveyImage);
            anchor.remove();
        } catch (_) {
            dispatch(alertActions.error(t('alertMessages.downloadFail')));
        }
    };

    return (
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {qrImage && <PreviewQRImg src={qrImage} alt="qr" />}
            <ButtonsWrapper>
                <Button
                    variant="outlined"
                    sx={{ backgroundColor: '#fff' }}
                    onClick={handleOpen}
                    startIcon={<DownloadIcon />}
                >
                    {t('buttons.download')}
                </Button>
                <Button
                    variant="outlined"
                    sx={{ backgroundColor: '#fff' }}
                    onClick={onPrint}
                    startIcon={<LocalPrintshopIcon />}
                >
                    {t('buttons.print')}
                </Button>
            </ButtonsWrapper>
            {anchorEl && (
                <ClickAwayListener onClickAway={handleClose}>
                    <Popper anchorEl={anchorEl} open={!!anchorEl}>
                        {['PNG', 'SVG', 'PDF'].map(ext => (
                            <MenuItem key={ext} onClick={() => onDownload(ext)}>
                                <Typography variant="body2">{ext}</Typography>
                            </MenuItem>
                        ))}
                    </Popper>
                </ClickAwayListener>
            )}
        </div>
    );
};

export default PreviewCustomization;
